import { DiagonalCropContainer, DiagonalCropImage, FallbackImage, Overlay } from './styles';
import { convertDegreesToRadians, isIEorEdge } from './utilities';
import PropTypes from 'prop-types';
import React from 'react';
import { Sides } from './constants';
import { ThemingContext } from '@doordash/design-language';

var DiagonalCrop = function DiagonalCrop(_ref) {
  var verticalWipePercentage = _ref.verticalWipePercentage,
      diagonalWipePercentage = _ref.diagonalWipePercentage,
      side = _ref.side,
      alt = _ref.alt,
      src = _ref.src,
      srcSet = _ref.srcSet,
      sizes = _ref.sizes,
      fallbackSrc = _ref.fallbackSrc,
      shouldAnimate = _ref.shouldAnimate,
      animationDuration = _ref.animationDuration,
      overlayColor = _ref.overlayColor,
      imagePosition = _ref.imagePosition,
      angle = _ref.angle,
      width = _ref.width,
      height = _ref.height;
  var theme = React.useContext(ThemingContext);
  return React.createElement(DiagonalCropContainer, {
    width: width,
    height: height
  }, isIEorEdge() ? React.createElement(FallbackImage, {
    src: fallbackSrc,
    imagePosition: imagePosition
  }) : React.createElement(DiagonalCropImage, {
    containerHeight: height,
    alt: alt,
    side: side,
    src: src,
    srcSet: srcSet,
    sizes: sizes,
    imagePosition: imagePosition,
    shouldAnimate: shouldAnimate,
    diagonalWipePercentage: diagonalWipePercentage,
    animationDuration: animationDuration,
    valueToTransition: ['clip-path', '-webkit-clip-path'],
    angle: convertDegreesToRadians(angle)
  }), React.createElement(Overlay, {
    shouldAnimate: shouldAnimate,
    animationDuration: animationDuration,
    valueToTransition: "transform",
    overlayColor: overlayColor || theme.Colors.DiagonalDefaultOverlay,
    verticalWipePercentage: verticalWipePercentage,
    side: side
  }));
};

DiagonalCrop.propTypes = {
  side: PropTypes.oneOf(Object.values(Sides)).isRequired,

  /**
   * Size (in width and height props) _must_ be provided.
   * We default to width=50vw and height=100vh,
   * but without these there is no intrinsic size of
   * the component.
   */
  width: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,

  /**
   * Angle can be provided; if an angle is provided, the
   * diagonal will always display with that angle preserved.
   * If this is not provided (and by default is undefined)
   * the angle will be fluid based on the height and width
   * provided.
   *
   * The unit of this value is in degrees.
   */
  angle: PropTypes.number,

  /**
   * The API for defining the image to be contained
   * is based on the <image> tag API. You _must_
   * provide a src image (for fallback) and you
   * _must_ provide a srcSet for responsive images.
   * Because imagery, particularly for marketing, can
   * be pretty large and heavy, we enforce providing
   * srcSet.
   *
   * fallbackSrc is the single image that you want
   * to show for browsers that don't support clip-path
   * (aka, IE 11 and Edge).
   *
   * alt is used to indicate to screenreaders
   * the content of the image.
   */
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string.isRequired,
  sizes: PropTypes.string,
  fallbackSrc: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,

  /**
   * imagePosition is used to art-direct how the image,
   * when sized to cover the element's height and width,
   * is positioned. Maps to the background-position CSS
   * syntax.
   */
  imagePosition: PropTypes.string,

  /**
   * verticalWipePercentage and diagonalWipePercentage
   * are numbers from 0 to 100 that determine whether
   * the image provided is hidden (0) or shown (100).
   *
   * By default, the image is totally shown, so both
   * of these default to 100.
   *
   * You should only ever manipulate one of these at a time.
   */
  verticalWipePercentage: PropTypes.number.isRequired,
  diagonalWipePercentage: PropTypes.number.isRequired,

  /**
   * shouldAnimate governs whether changing either
   * verticalWipePercentage or diagonalWipePercentage
   * should happen with or without interpolation.
   *
   * By default, shouldAnimate=true, so if you
   * change either of those from 100 -> 0, the change
   * will animate for you.
   *
   * Change shouldAnimate=false if you are
   * building a continuous animation based on interaction
   * (like a scroll animation or a swipe animation).
   */
  shouldAnimate: PropTypes.bool.isRequired,
  animationDuration: PropTypes.string,

  /**
   * overlayColor should match whatever color
   * is behind the element. By default this is
   * overlayColor=Colors.DiagonalDefaultOverlay, but you should
   * update this as needed. This is needed because the
   * verticalWipe uses an element with a solid color layered
   * above the image to reveal/hide it.
   */
  overlayColor: PropTypes.string
};
DiagonalCrop.defaultProps = {
  width: '50vw',
  height: '100vh',
  imagePosition: 'center',
  verticalWipePercentage: 100,
  diagonalWipePercentage: 100,
  shouldAnimate: true,
  animationDuration: '800ms'
};
DiagonalCrop.Sides = Sides;
export default DiagonalCrop;